import * as THREE from 'three';
import { Graph3DNode } from './useGraph3DData';
import { MeshShape, getKindConfig } from './constants';

const geometryCache = new Map<string, THREE.BufferGeometry>();

function getGeometry(shape: MeshShape, size: number): THREE.BufferGeometry {
  const key = `${shape}:${size.toFixed(1)}`;
  if (geometryCache.has(key)) return geometryCache.get(key)!;

  let geo: THREE.BufferGeometry;
  switch (shape) {
    case 'octahedron':
      geo = new THREE.OctahedronGeometry(size);
      break;
    case 'icosahedron':
      geo = new THREE.IcosahedronGeometry(size, 1);
      break;
    case 'torus':
      geo = new THREE.TorusGeometry(size * 0.8, size * 0.3, 8, 16);
      break;
    case 'box':
      geo = new THREE.BoxGeometry(size * 1.4, size * 1.4, size * 1.4);
      break;
    case 'cylinder':
      geo = new THREE.CylinderGeometry(size * 0.7, size * 0.7, size * 1.4, 8);
      break;
    case 'cone':
      geo = new THREE.ConeGeometry(size, size * 2, 8);
      break;
    case 'dodecahedron':
      geo = new THREE.DodecahedronGeometry(size);
      break;
    case 'sphere':
    default:
      geo = new THREE.SphereGeometry(size, 8, 8);
      break;
  }

  geometryCache.set(key, geo);
  return geo;
}

export function createNodeObject(node: Graph3DNode, isSelected = false): THREE.Object3D {
  const cfg = getKindConfig(node.kind);
  const emissiveColor = isSelected
    ? new THREE.Color(node.glowColor)
    : new THREE.Color(node.glowColor).multiplyScalar(0.3);

  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(node.color),
    emissive: emissiveColor,
    emissiveIntensity: isSelected ? 1.5 : 0.4,
    roughness: 0.4,
    metalness: 0.3,
    transparent: true,
    opacity: 0.92,
  });

  const geo = getGeometry(cfg.mesh, node.size);
  const mesh = new THREE.Mesh(geo, material);
  mesh.userData = { nodeId: node.id };
  return mesh;
}

export function disposeNodeGeometries() {
  geometryCache.forEach(geo => geo.dispose());
  geometryCache.clear();
}
